import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  Divider,
  Stack,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  IconButton
} from '@chakra-ui/react';
import { BadgeCheck, BadgeInfo, BadgeX, Download, Calendar } from 'lucide-react';
import { FaIdCard, FaMapMarkerAlt, FaRegCheckCircle, FaCalendarAlt } from 'react-icons/fa';
import NoDataFound from 'components/NoDataGraphics/NoDataFound';
import DocumentViewer from 'components/DocumentViewer/DocumentViewer';
import { formatDate } from 'utils/dateFormatter';
import ReusableAvatar from 'components/Avatar/ReusableAvatar';
import InfoRow from 'components/InfoRow/InfoRow';
import DocumentIcon from 'components/DocumentViewer/DocumentIcon';

const ViewBusiness = ({ business, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDocument, setCurrentDocument] = useState(null);

  if (!business) return null;

  const {
    name_of_company,
    cipa_number,
    position_held,
    place_of_operation,
    date_of_incorporation,
    created_at,
    status,
    documents,
  } = business;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Open document modal
  const handleDocumentClick = (doc) => {
    setCurrentDocument({
      url: `${BASE_URL.replace(/\/$/, '')}/${doc.file_path.replace(/^\//, '')}`,
      type: doc.file_extension,       // pdf, jpeg, docx etc.
      title: doc.document_type,       // document name/title
      dateUploaded: formatDate(doc.uploaded_at),
    });
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setCurrentDocument(null);
  };

  const downloadDocument = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get company logo if uploaded
  const companyLogoDoc = documents?.find(doc => doc.document_type === 'Company Logo');
  const companyLogoUrl = companyLogoDoc
    ? `${BASE_URL.replace(/\/$/, '')}/${companyLogoDoc.file_path.replace(/^\//, '')}`
    : null;

  return (
    <Flex direction="column" p={8} width="100%">
      {/* Header */}
      <Flex alignItems="center">
        <ReusableAvatar src={companyLogoUrl} />
        <Box ml={4}>
          <Text fontSize="2xl" fontWeight="bold" display="flex" alignItems="center">
            {name_of_company}
            {status === 'Approved' && <StatusBadge color="green" label="Approved" />}
            {status === 'Pending' && <StatusBadge color="yellow" label="Pending" />}
            {status === 'Suspended' && <StatusBadge color="red" label="Suspended" />}
            {status === 'Rejected' && <StatusBadge color="gray" label="Rejected" />}
            {status === 'Deactivated' && <StatusBadge color="gray" label="Deactivated" />}
          </Text>
          <Text fontSize="sm" color="gray.500" mt={1}>{place_of_operation}</Text>
        </Box>
      </Flex>

      <Flex mt={2} alignItems="center">
        <Icon as={Calendar} size={16} color="gray.500" mr={1} />
        <Text fontSize="sm" color="gray.500">
          Date Incorporated: {formatDate(date_of_incorporation)}
        </Text>
      </Flex>

      <Divider my={6} />

      {/* Business Info */}
      <Box mb={6}>
        <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>Business Info</Text>
        <Stack spacing={3}>
          <InfoRow icon={FaIdCard} label="CIPA Number" value={cipa_number} />
          <InfoRow icon={FaMapMarkerAlt} label="Place of Operation" value={place_of_operation} />
          <InfoRow icon={FaRegCheckCircle} label="Business Status" value={status} />
          <InfoRow icon={FaCalendarAlt} label="Date Created" value={formatDate(created_at)} />
        </Stack>
      </Box>

      <Divider my={6} />

      {/* Documents */}
      <Box mb={6}>
        <Text fontSize="md" fontWeight="500" color="gray.500" mb={2}>Business Documents</Text>
        {documents && documents.length > 0 ? (
          <Flex wrap="wrap" gap={4}>
            {documents.map((doc) => (
              <Tooltip label="Click to view" hasArrow placement="top" key={doc.document_id}>
                <Box
                  p={2}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  _hover={{ cursor: 'pointer', bg: 'gray.100' }}
                  onClick={() => handleDocumentClick(doc)}
                  w="calc(50% - 8px)"
                >
                  <DocumentIcon type={doc.file_extension} />
                  <Box ml={3}>
                    <Text fontWeight="bold" color="gray.600">{doc.document_type}</Text>
                    <Text color="gray.500" fontSize="sm">
                      Uploaded: {formatDate(doc.uploaded_at)}
                    </Text>
                  </Box>
                </Box>
              </Tooltip>
            ))}
          </Flex>
        ) : (
          <NoDataFound message="No documents uploaded" width="100px" height="100px" fontSize="sm" fontWeight="bold" />
        )}
      </Box>

      {/* Document Viewer Modal */}
      <Modal isOpen={isOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent maxWidth="500px" maxHeight="80vh" overflowY="auto">
          <ModalHeader fontSize="16px">Document Viewer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {currentDocument ? (
              <>
                <DocumentViewer documents={[currentDocument]} />
                <IconButton
                  aria-label="Download Document"
                  icon={<Download />}
                  onClick={() => downloadDocument(currentDocument.url, currentDocument.title)}
                  bgGradient="linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)"
                  color="white"
                  mt={4}
                  _hover={{ bgGradient: "linear-gradient(134.23deg, #071D54 14.41%, #1E3A8A 85.58%)" }}
                  boxShadow="md"
                  borderRadius="full"
                  size="lg"
                />
              </>
            ) : (
              <NoDataFound message="No document to display." />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

const StatusBadge = ({ color, label }) => (
  <Flex alignItems="center" ml={1}>
    <Icon as={BadgeCheck} size={12} color={`${color}.500`} stroke="white" fill={`${color}.500`} />
    <Text color={`${color}.500`} fontWeight="xs" fontSize="13px" ml={1}>{label}</Text>
  </Flex>
);

export default ViewBusiness;
